import { useRouter } from 'next/router'

import { useIntl } from 'react-intl'
import { createParam } from 'solito'

import { Theme, useMediaQuery } from '@mui/material'

import useFormQuantity from 'app/features/ListingFeature/hooks/useFormQuantity'
import useCurrencyState from 'app/hooks/useCurrencyState'
import useEffectAfterMount from 'app/hooks/useEffectAfterMount'
import {
  getViewItemEventParams,
  useSendGoogleAnalyticsEvent
} from 'app/hooks/useSendGoogleAnalyticsEvent'
import { useGetListing } from 'app/services/Query/listing/listing.query'
import useAuthStore, { getAuthUser, getIsConfirmed } from 'app/store/auth.store'
import { IS_BROWSER } from 'app/utils/constants/env.constants'
import { rIdleCallback } from 'app/utils/helpers/functions.helpers'
import { hasStatusError } from 'app/utils/helpers/type.helpers'
import { hasGuestStatus } from 'app/utils/helpers/user.helpers'

import { getListingMetaData } from '../utils/meta.helpers'

const { useParam } = createParam()

export const useListingView = () => {
  const { formatMessage } = useIntl()
  const { isFallback, replace } = useRouter()
  const { sendGA4Event } = useSendGoogleAnalyticsEvent()

  const [slug = ''] = useParam('slug')

  const { currency } = useCurrencyState()
  const isAuth = useAuthStore(getIsConfirmed)
  const user = useAuthStore(getAuthUser)
  const isGuestUser = hasGuestStatus(user?.status)
  const userId = isGuestUser ? undefined : user?.id
  const isGTMLoaded = IS_BROWSER && !!window?.dataLayer

  const isMobile = useMediaQuery(
    (theme: Theme) => theme.breakpoints.down('md'),
    { noSsr: true }
  )
  const isLargeSize = useMediaQuery(
    (theme: Theme) => theme.breakpoints.down('lg'),
    { noSsr: true }
  )

  const {
    data: listing,
    isFetchedAfterMount,
    refetch
  } = useGetListing({
    slug: slug,
    options: {
      enabled: !!slug,
      onError: (error) => {
        if (hasStatusError(error) && error?.status === 406) {
          replace('/404')
        }
      }
    }
  })

  useEffectAfterMount(() => {
    if (currency && !!slug) refetch()
  }, [currency])

  useEffectAfterMount(() => {
    const isListingRefetch = isAuth && isFetchedAfterMount
    if (isListingRefetch) {
      rIdleCallback(() => refetch())
    }
  }, [isAuth, isFetchedAfterMount])

  useEffectAfterMount(() => {
    if (!listing?.id || !isFetchedAfterMount || !isGTMLoaded) return

    const ecommerceParams = getViewItemEventParams(listing)

    sendGA4Event({
      eventName: 'view_item',
      ...ecommerceParams,
      user_id: userId
    })
  }, [isFetchedAfterMount, isGTMLoaded])

  const availableQuantity = listing?.available_quantity
  const methods = useFormQuantity({ availableQuantity })

  const { metaTitle, metaDescription, metaImages } = getListingMetaData({
    listing,
    formatMessage
  })

  return {
    isFallback,
    isLargeSize,
    isMobile,
    listing,
    methods,
    slug,
    metaImages,
    metaTitle,
    metaDescription
  }
}
